import React, { useCallback, useMemo, useState } from "react";
import { GlobalTableActionButton } from "../../../../global/GlobalTableActionButton";
import {
  GlobalDirectoryShell,
  GlobalDirectoryFiltersSection,
  GlobalDirectoryGridSection,
  GlobalDirectoryPaginationSection,
  GlobalDirectoryColumnPicker,
  GlobalDirectoryExportMenu,
} from "../../../../global/directory";
import "../../../user-builder/styles/userBuilderTable.css";
import "../../../../global/directory/globalDirectory.css";
import MyTournamentsDetailModal from "./MyTournamentsDetailModal";
import MyTournamentsCertificateButton from "./MyTournamentsCertificateButton";
import {
  MY_TOURNAMENTS_DIRECTORY_COLUMN_DEFS,
  MY_TOURNAMENTS_DIRECTORY_VISIBILITY_KEY,
  MY_TOURNAMENTS_PAGE_SIZE,
  buildValueOptionsFromRows,
  createInitialColumnVisibility,
  defaultMyTournamentsFilters,
  directoryCellValue,
  filterDirectoryRows,
  paginateList,
  resultBadgeClass,
  payChipClass,
  tournamentStatusBadgeClass,
} from "../../utils/myTournamentsDirectoryHelpers";

export default function MyTournamentsDirectorySection({ directory, loading, onFeedback }) {
  const rows = useMemo(
    () => (Array.isArray(directory) ? directory : []),
    [directory]
  );
  const [selected, setSelected] = useState(null);
  const [filters, setFilters] = useState(defaultMyTournamentsFilters);
  const [page, setPage] = useState(1);
  const [columnVisibility, setColumnVisibility] = useState(() =>
    createInitialColumnVisibility(
      MY_TOURNAMENTS_DIRECTORY_VISIBILITY_KEY,
      MY_TOURNAMENTS_DIRECTORY_COLUMN_DEFS
    )
  );

  const filtered = useMemo(
    () => filterDirectoryRows(rows, filters, directoryCellValue),
    [rows, filters]
  );

  const { page: safePage, totalPages, total, slice } = useMemo(
    () => paginateList(filtered, page, MY_TOURNAMENTS_PAGE_SIZE),
    [filtered, page]
  );

  const valueFilterOptions = useMemo(() => {
    const col = filters.column;
    if (!col) return [];
    return buildValueOptionsFromRows(rows, col, directoryCellValue);
  }, [filters.column, rows]);

  const visibleColumns = useMemo(
    () => MY_TOURNAMENTS_DIRECTORY_COLUMN_DEFS.filter((c) => columnVisibility[c.id]),
    [columnVisibility]
  );

  const tableHeaders = useMemo(
    () => [
      ...visibleColumns.map((c) => ({ key: c.id, label: c.label })),
      { key: "actions", label: "Actions", className: "ub-table__col-actions" },
    ],
    [visibleColumns]
  );

  const getExportData = useCallback(() => {
    const headers = visibleColumns.map((c) => c.label);
    const dataRows = filtered.map((row) =>
      visibleColumns.map((c) => directoryCellValue(c.id, row))
    );
    return { headers, rows: dataRows };
  }, [filtered, visibleColumns]);

  const showRows = !loading && slice.length > 0;

  if (!loading && rows.length === 0) {
    return (
      <section className="user-builder-panel mm-directory-wrap" aria-label="Tournament directory">
        <div className="user-builder-panel__head">
          <h3 className="user-builder-panel__title">Tournament directory</h3>
          <p className="user-builder-panel__hint">
            Browse all tournaments you have registered for.
          </p>
        </div>
        <p className="mm-empty">No tournament registrations yet.</p>
      </section>
    );
  }

  return (
    <section className="user-builder-panel mm-directory-wrap" aria-label="Tournament directory">
      <div className="user-builder-panel__head">
        <h3 className="user-builder-panel__title">Tournament directory</h3>
        <p className="user-builder-panel__hint">
          Search, filter, and browse your tournament registrations.
        </p>
      </div>

      <GlobalDirectoryShell
        className="mm-directory-shell"
        filters={
          <GlobalDirectoryFiltersSection
            disabled={loading}
            className="ub-filters ub-user-directory-filters"
            searchLabel="Search tournaments"
            searchPlaceholder="ID, name, sport, payment, receipt, transaction…"
            searchValue={filters.search}
            onSearchChange={(v) => {
              setFilters((f) => ({ ...f, search: v }));
              setPage(1);
            }}
            columnLabel="Column"
            columnValue={filters.column}
            onColumnChange={(v) => {
              setFilters((f) => ({ ...f, column: v, value: "" }));
              setPage(1);
            }}
            gridColumnDefs={MY_TOURNAMENTS_DIRECTORY_COLUMN_DEFS}
            valueLabel="Value"
            valueValue={filters.value}
            onValueChange={(v) => {
              setFilters((f) => ({ ...f, value: v }));
              setPage(1);
            }}
            valueOptions={valueFilterOptions}
            onClear={() => {
              setFilters(defaultMyTournamentsFilters());
              setPage(1);
            }}
            clearFilterLabel="Clear filter"
            secondaryStart={
              <GlobalDirectoryColumnPicker
                columnDefs={MY_TOURNAMENTS_DIRECTORY_COLUMN_DEFS}
                visibility={columnVisibility}
                onVisibilityChange={setColumnVisibility}
                storageKey={MY_TOURNAMENTS_DIRECTORY_VISIBILITY_KEY}
              />
            }
            secondaryEnd={
              <GlobalDirectoryExportMenu
                getExportData={getExportData}
                filenameBase={`my-tournaments-${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}`}
                buttonLabel="Export as"
              />
            }
          />
        }
        grid={
          <GlobalDirectoryGridSection
            loading={loading}
            loadingMessage="Loading tournament directory…"
            headers={tableHeaders}
            isEmpty={!loading && filtered.length === 0}
            emptyMessage="No tournaments match your filters."
            tableAriaLabel="My tournaments directory"
            wrapClassName="ub-table-wrap"
            tableClassName="ub-table"
          >
            {showRows
              ? slice.map((row) => (
                  <tr key={row.id}>
                    {visibleColumns.map((col) => (
                      <td key={col.id}>
                        {col.id === "status" ? (
                          <span className={tournamentStatusBadgeClass(row.status)}>
                            {directoryCellValue(col.id, row)}
                          </span>
                        ) : col.id === "result" ? (
                          <span className={resultBadgeClass(row.result)}>
                            {directoryCellValue(col.id, row)}
                          </span>
                        ) : col.id === "payment_status" ? (
                          <span className={payChipClass(row.payment_status)}>
                            {directoryCellValue(col.id, row)}
                          </span>
                        ) : (
                          directoryCellValue(col.id, row)
                        )}
                      </td>
                    ))}
                    <td className="ub-table__col-actions">
                      <div className="ub-table-actions mt-directory-actions">
                        <GlobalTableActionButton
                          action="view"
                          variant="ghost"
                          onClick={() => setSelected(row)}
                        />
                        <MyTournamentsCertificateButton
                          row={row}
                          onFeedback={onFeedback}
                        />
                      </div>
                    </td>
                  </tr>
                ))
              : null}
          </GlobalDirectoryGridSection>
        }
        pagination={
          !loading && filtered.length > 0 ? (
            <GlobalDirectoryPaginationSection
              page={safePage}
              totalPages={totalPages}
              total={total}
              pageSize={MY_TOURNAMENTS_PAGE_SIZE}
              onPageChange={setPage}
              label="Tournament directory pagination"
            />
          ) : null
        }
      />

      <MyTournamentsDetailModal
        row={selected}
        onClose={() => setSelected(null)}
        onFeedback={onFeedback}
      />
    </section>
  );
}
